import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import PortalShell from "./PortalShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Package, DollarSign, TrendingUp, Upload, CheckCircle2 } from "lucide-react";

const BYTES_PER_KB = 1024;

export default function CarrierPortal() {
  return (
    <PortalShell kind="carrier">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="loads" element={<Loads />} />
        <Route path="documents" element={<Documents />} />
        <Route path="invoices" element={<Payments />} />
        <Route path="banking" element={<Banking />} />
      </Routes>
    </PortalShell>
  );
}

function Overview() {
  const [avail, setAvail] = useState([]);
  const [mine, setMine] = useState([]);
  const [pay, setPay] = useState([]);
  useEffect(() => {
    api.get("/loads/available").then(r => setAvail(r.data)).catch(() => {});
    api.get("/loads/mine").then(r => setMine(r.data)).catch(() => {});
    api.get("/carrier/payments").then(r => setPay(r.data)).catch(() => {});
  }, []);
  const pending = pay.filter(p => p.status !== "Paid").reduce((s, p) => s + p.amount, 0);
  return (
    <div className="space-y-6" data-testid="carrier-overview">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat icon={Package} label="Available loads" value={avail.length} />
        <Stat icon={TrendingUp} label="My loads" value={mine.length} />
        <Stat icon={DollarSign} label="Pending payments" value={`$${pending.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
        <Stat icon={CheckCircle2} label="Payments count" value={pay.length} />
      </div>
      <Panel title="Newest available loads">
        {avail.slice(0, 5).map(l => <LoadRow key={l.load_id} l={l} showAccept />)}
        {!avail.length && <Empty>No loads currently posted.</Empty>}
      </Panel>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5">
    <Icon className="h-5 w-5 text-[#2E7BC7]" />
    <div className="mt-3 font-label text-xs text-slate-500">{label}</div>
    <div className="mt-1 font-display font-black text-2xl text-[#0B3D6E]">{value}</div>
  </div>
);
const Panel = ({ title, children, cta }) => (
  <div className="bg-white border border-gray-200 rounded-lg">
    <div className="flex items-center justify-between p-5 border-b border-gray-100">
      <div className="font-display font-bold text-[#0B3D6E]">{title}</div>{cta}
    </div>
    <div>{children}</div>
  </div>
);
const Empty = ({ children }) => <div className="p-8 text-center text-sm text-slate-500">{children}</div>;

function LoadRow({ l, showAccept, onAccept }) {
  return (
    <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center gap-3 md:justify-between" data-testid={`load-row-${l.load_id}`}>
      <div>
        <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{l.load_id}</div>
        <div className="text-xs text-slate-500 mt-1">{l.origin} → {l.destination} · {l.equipment_type} · {l.commodity}</div>
        <div className="text-xs text-slate-400">Pickup {l.pickup_date} · {l.miles} mi</div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="font-display font-bold text-[#0B3D6E]">${l.rate?.toLocaleString()}</div>
          <div className="text-xs text-slate-500">{l.status}</div>
        </div>
        {showAccept && l.status === "Available" && (
          <button className="btn-primary-apex text-xs" onClick={() => onAccept?.(l.load_id)} data-testid={`accept-load-${l.load_id}`}>Accept</button>
        )}
      </div>
    </div>
  );
}

function Loads() {
  const [avail, setAvail] = useState([]);
  const [mine, setMine] = useState([]);
  const load = () => {
    api.get("/loads/available").then(r => setAvail(r.data)).catch(() => {});
    api.get("/loads/mine").then(r => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const accept = async (id) => {
    try { await api.post("/loads/accept", { load_id: id }); toast.success("Load accepted"); load(); }
    catch { toast.error("Could not accept load"); }
  };
  return (
    <div className="space-y-6">
      <Panel title="Available loads">
        {avail.map(l => <LoadRow key={l.load_id} l={l} showAccept onAccept={accept} />)}
        {!avail.length && <Empty>All loads have been booked.</Empty>}
      </Panel>
      <Panel title="My loads">
        {mine.map(l => <LoadRow key={l.load_id} l={l} />)}
        {!mine.length && <Empty>Accept a load to see it here.</Empty>}
      </Panel>
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("insurance");
  const load = () => api.get("/documents/mine").then(r => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("document_type", type);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch { toast.error("Upload failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="font-display font-bold text-[#0B3D6E] mb-3">Upload compliance / freight document</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="carrier-doc-type">
            <option value="insurance">Insurance certificate</option>
            <option value="W9">W9 / Tax form</option>
            <option value="BOL">Bill of Lading</option>
            <option value="POD">Proof of Delivery</option>
            <option value="authority">Operating Authority</option>
          </select>
          <label className="btn-primary-apex text-sm cursor-pointer inline-flex items-center gap-2">
            <Upload className="h-4 w-4" /> Choose file
            <input type="file" className="hidden" onChange={onFile} data-testid="carrier-doc-input" />
          </label>
        </div>
      </div>
      <Panel title="My uploads">
        {docs.map(d => (
          <div key={d.doc_id} className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div><div className="font-semibold text-sm text-[#0B3D6E]">{d.filename}</div><div className="text-xs text-slate-500">{d.document_type} · {(d.size_bytes / BYTES_PER_KB).toFixed(1)} KB</div></div>
            <div className="text-xs text-slate-500">{new Date(d.uploaded_at).toLocaleDateString()}</div>
          </div>
        ))}
        {!docs.length && <Empty>Upload insurance and compliance documents.</Empty>}
      </Panel>
    </div>
  );
}

function Payments() {
  const [pay, setPay] = useState([]);
  useEffect(() => { api.get("/carrier/payments").then(r => setPay(r.data)).catch(() => {}); }, []);
  return (
    <Panel title="Settlement history">
      {pay.map(p => (
        <div key={p.payment_id} className="p-4 border-b border-gray-100 flex items-center justify-between" data-testid={`payment-${p.payment_id}`}>
          <div>
            <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{p.payment_id}</div>
            <div className="text-xs text-slate-500">Load ref: {p.load_ref}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-display font-bold text-[#0B3D6E]">${p.amount.toFixed(2)}</div>
            <span className={`pill ${p.status === "Paid" ? "bg-green-100 text-green-800" : p.status === "Processing" ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>{p.status}</span>
          </div>
        </div>
      ))}
      {!pay.length && <Empty>Payments will appear here.</Empty>}
    </Panel>
  );
}

function Banking() {
  const [profile, setProfile] = useState({});
  const [f, setF] = useState({ account_holder: "", bank_name: "", account_number: "", routing_number: "" });
  useEffect(() => { api.get("/carrier/profile").then(r => setProfile(r.data || {})).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/carrier/banking", f); toast.success("Banking updated"); setProfile({ ...profile, ...f, account_number_masked: data.account_number_masked }); setF({ ...f, account_number: "" }); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-testid="banking-form">
        <div className="font-display font-bold text-[#0B3D6E]">Update banking</div>
        <Field label="Account holder" v={f.account_holder} on={(e) => setF({ ...f, account_holder: e.target.value })} test="bank-holder" />
        <Field label="Bank name" v={f.bank_name} on={(e) => setF({ ...f, bank_name: e.target.value })} test="bank-name" />
        <Field label="Account number" v={f.account_number} on={(e) => setF({ ...f, account_number: e.target.value })} test="bank-account" />
        <Field label="Routing number" v={f.routing_number} on={(e) => setF({ ...f, routing_number: e.target.value })} test="bank-routing" />
        <button className="btn-primary-apex" data-testid="bank-save-btn">Save</button>
      </form>
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="font-display font-bold text-[#0B3D6E]">Current on file</div>
        <div className="mt-4 space-y-2 text-sm">
          <Row k="Holder" v={profile.account_holder || "—"} />
          <Row k="Bank" v={profile.bank_name || "—"} />
          <Row k="Account" v={profile.account_number_masked || "—"} />
          <Row k="Routing" v={profile.routing_number || "—"} />
          <Row k="Updated" v={profile.updated_at ? new Date(profile.updated_at).toLocaleString() : "—"} />
        </div>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => <div className="flex justify-between border-b border-gray-100 py-2"><span className="text-slate-500">{k}</span><span className="text-[#0B3D6E] font-medium">{v}</span></div>;
function Field({ label, v, on, test }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</label>
      <input value={v} onChange={on} required className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] outline-none" data-testid={test} />
    </div>
  );
}
